/**
 * Properties panel — form, card, or control, depending on the selection.
 *
 * A bound control shows its binding (`patients.mrn`) as read-only text. The
 * binding is a reference to a live PostgreSQL column, not something the form
 * owns, so it is changed by removing the control and dropping the field you
 * want — never by retyping a column name here.
 *
 * Properties are grouped — Identity, Binding, Appearance, Layout — so a long
 * list reads as a few short ones. The grouping is visual: the property model
 * itself is unchanged, and every property still writes straight through to the
 * selected card or control.
 *
 * Width and Height are grid units. There is no pixel offset to edit.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, SlidersHorizontal } from "lucide-react";
import { FD } from "@/constants/testIds";
import {
  CARD_MAX_ROWS,
  CONTROL_TYPES,
  DATA_CONTROL_TYPES,
  GRID_MIN_COLUMNS,
  alignCardsBottom,
  alignCardsLeft,
  alignCardsRight,
  alignCardsTop,
  cardBounds,
  controlBinding,
  findCard,
  findControl,
  formBounds,
  isBindableControl,
  withCardGeometry,
} from "@/lib/formDesigner";
import { describeField } from "@/lib/formDesignerFields";

function NumberField({ label, testId, value, min, max, onChange, disabled }) {
  return (
    <div className="efd__field">
      <label>{label}</label>
      <input
        type="number"
        data-testid={testId}
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = Number.parseInt(event.target.value, 10);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </div>
  );
}

/**
 * A titled run of related properties, open by default and collapsible.
 *
 * Grouping and collapsing are presentation only: every field inside still
 * writes straight through to the selected card or control, and closing a
 * section changes nothing about the form.
 */
function Group({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <section className={`efd__group${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="efd__group-title"
        aria-expanded={open}
        title={open ? `Collapse ${title}` : `Expand ${title}`}
        onClick={() => setOpen((current) => !current)}
      >
        <ChevronDown className="efd__group-caret" aria-hidden="true" />
        {title}
      </button>
      {open ? <div className="efd__group-body">{children}</div> : null}
    </section>
  );
}

export default function FormDesignerProperties({
  form,
  fields = [],
  selection,
  selectedCardIds = [],
  onCardChange,
  onControlChange,
  onMoveCard,
  onAlign,
  onDeleteCard,
  onDeleteControl,
  onReassignControl,
  onCollapse,
  readOnly = false,
}) {
  const card = selection?.kind === "card" ? findCard(form, selection.id) : null;
  const control = selection?.kind === "control" ? findControl(form, selection.id) : null;
  const kind = control ? "control" : card ? "card" : "form";
  const geom = card ? withCardGeometry(card, formBounds(form).columns) : null;
  const canvas = formBounds(form);
  const canAlign = !readOnly && selectedCardIds.length >= 2;

  return (
    <aside className="efd__props efd__panel" data-testid={FD.props}>
      <div className="efd__panel-head">
        <h3 className="efd__panel-title">
          <SlidersHorizontal className="efd__icon" aria-hidden="true" />
          Properties
        </h3>
        <span className="efd__props-kind" data-testid={FD.propsKind}>
          {kind === "control" ? "Control" : kind === "card" ? "Card" : "Form"}
        </span>
        <button
          type="button"
          className="efd__icon-btn"
          data-testid={FD.propsCollapse}
          aria-label="Collapse Properties"
          title="Collapse Properties"
          onClick={onCollapse}
        >
          <ChevronRight className="efd__icon" aria-hidden="true" />
        </button>
      </div>

      {kind === "form" ? (
        <Group title="Identity">
          <div className="efd__field">
            <label>Primary Table</label>
            <span className="efd__prop-value">{form.primaryTable || "Select a table"}</span>
          </div>
          <div className="efd__field">
            <label>Cards</label>
            <span className="efd__prop-value">{(form.cards || []).length}</span>
          </div>
          <div className="efd__field">
            <label>Controls</label>
            <span className="efd__prop-value">{(form.controls || []).length}</span>
          </div>
          <p className="efd__props-note">
            Select a card or a control on the canvas to edit its properties. Form Name, Primary Table and
            Description are in the toolbar above.
          </p>
        </Group>
      ) : null}

      {kind === "card" ? (
        <>
          <Group title="Identity">
            <div className="efd__field">
              <label>Name</label>
              <input
                data-testid={FD.cardPropTitle}
                value={card.title}
                disabled={readOnly}
                onChange={(event) => onCardChange(card.id, { title: event.target.value })}
              />
            </div>
          </Group>
          <Group title="Appearance">
            <label className="efd__toggle">
              <input
                type="checkbox"
                data-testid={FD.cardPropCollapsible}
                checked={Boolean(card.collapsible)}
                disabled={readOnly}
                onChange={(event) => onCardChange(card.id, { collapsible: event.target.checked })}
              />
              Collapsible
            </label>
          </Group>
          <Group title="Layout">
            <NumberField
              label="Column"
              testId={FD.cardPropCol}
              value={geom.col}
              min={1}
              max={canvas.columns}
              disabled={readOnly}
              onChange={(next) => onCardChange(card.id, { col: next })}
            />
            <NumberField
              label="Row"
              testId={FD.cardPropRow}
              value={geom.row}
              min={1}
              max={canvas.rows}
              disabled={readOnly}
              onChange={(next) => onCardChange(card.id, { row: next })}
            />
            <NumberField
              label="Width (cols)"
              testId={FD.cardPropWidth}
              value={geom.width}
              min={GRID_MIN_COLUMNS}
              max={canvas.columns}
              disabled={readOnly}
              onChange={(next) => onCardChange(card.id, { columns: next, width: next })}
            />
            <NumberField
              label="Height (rows)"
              testId={FD.cardPropHeight}
              value={geom.height}
              min={1}
              max={canvas.rows}
              disabled={readOnly}
              onChange={(next) => onCardChange(card.id, { height: next })}
            />
            <NumberField
              label="Columns"
              testId={FD.cardPropColumns}
              value={geom.columns}
              min={GRID_MIN_COLUMNS}
              max={canvas.columns}
              disabled={readOnly}
              onChange={(next) => onCardChange(card.id, { columns: next, width: next })}
            />
          </Group>
          <div className="efd__prop-actions">
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.alignLeft}
              disabled={!canAlign}
              onClick={() => onAlign(alignCardsLeft)}
            >
              Align Left
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.alignRight}
              disabled={!canAlign}
              onClick={() => onAlign(alignCardsRight)}
            >
              Align Right
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.alignTop}
              disabled={!canAlign}
              onClick={() => onAlign(alignCardsTop)}
            >
              Align Top
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.alignBottom}
              disabled={!canAlign}
              onClick={() => onAlign(alignCardsBottom)}
            >
              Align Bottom
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.cardMoveUp}
              disabled={readOnly}
              onClick={() => onMoveCard(card.id, -1)}
            >
              Move Up
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost"
              data-testid={FD.cardMoveDown}
              disabled={readOnly}
              onClick={() => onMoveCard(card.id, 1)}
            >
              Move Down
            </button>
            <button
              type="button"
              className="efd__btn efd__btn--ghost efd__btn--danger"
              data-testid={FD.cardDelete}
              disabled={readOnly}
              onClick={() => onDeleteCard(card.id)}
            >
              Delete Card
            </button>
          </div>
        </>
      ) : null}

      {kind === "control" ? (
        <>
          <Group title="Identity">
            <div className="efd__field">
              <label>Control Type</label>
              <select
                data-testid={FD.controlPropType}
                value={control.controlType}
                disabled={readOnly}
                onChange={(event) => onControlChange(control.id, { controlType: event.target.value })}
              >
                {(control.field ? DATA_CONTROL_TYPES : CONTROL_TYPES).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="efd__field">
              <label>Name</label>
              <input
                data-testid={FD.controlPropName}
                value={control.name || ""}
                disabled={readOnly}
                onChange={(event) => onControlChange(control.id, { name: event.target.value })}
              />
            </div>
            <div className="efd__field">
              <label>Caption / Label</label>
              <input
                data-testid={FD.controlPropLabel}
                value={control.label}
                disabled={readOnly}
                onChange={(event) => onControlChange(control.id, { label: event.target.value })}
              />
            </div>
          </Group>

          <Group title="Binding">
            {control.binding ? (
              <>
                <div className="efd__field">
                  <label>Field</label>
                  <span className="efd__prop-value" data-testid={FD.controlPropSemanticField}>
                    {control.binding.label}
                  </span>
                </div>
                <div className="efd__field">
                  <label>FHIR Path</label>
                  <span className="efd__prop-value efd__prop-value--binding" data-testid={FD.controlPropFhirPath}>
                    {control.binding.fhirPath}
                  </span>
                </div>
                <div className="efd__field">
                  <label>Resource</label>
                  <span className="efd__prop-value" data-testid={FD.controlPropResourceType}>
                    {control.binding.resourceType}
                  </span>
                </div>
                <div className="efd__field">
                  <label>Field Type</label>
                  <span className="efd__prop-value" data-testid={FD.controlPropFieldType}>
                    {control.binding.fieldType}
                  </span>
                </div>
              </>
            ) : null}
            <div className="efd__field">
              <label>Bound Field</label>
              {isBindableControl(control.controlType) ? (
                <select
                  data-testid={FD.controlPropBindField}
                  value={control.field || ""}
                  disabled={readOnly}
                  onChange={(event) => onControlChange(control.id, { field: event.target.value })}
                >
                  <option value="">Not bound</option>
                  {fields.map((field) => (
                    <option key={field.name} value={field.name}>
                      {describeField(field).displayLabel}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="efd__prop-value" data-testid={FD.controlPropField}>
                  Not bound to the database
                </span>
              )}
            </div>
            {control.field || control.binding ? (
              <div className="efd__field">
                <label>Binding</label>
                <span className="efd__prop-value efd__prop-value--binding" data-testid={FD.controlPropField}>
                  {controlBinding(form, control)}
                </span>
              </div>
            ) : isBindableControl(control.controlType) ? (
              <div className="efd__field">
                <span className="efd__prop-value" data-testid={FD.controlPropField}>
                  Not bound to the database
                </span>
              </div>
            ) : null}
            {isBindableControl(control.controlType) ? (
              <>
                <label className="efd__toggle">
                  <input
                    type="checkbox"
                    data-testid={FD.controlPropRequired}
                    checked={Boolean(control.required)}
                    disabled={readOnly}
                    onChange={(event) => onControlChange(control.id, { required: event.target.checked })}
                  />
                  Required
                </label>
                <label className="efd__toggle">
                  <input
                    type="checkbox"
                    data-testid={FD.controlPropReadOnly}
                    checked={Boolean(control.readOnly)}
                    disabled={readOnly}
                    onChange={(event) => onControlChange(control.id, { readOnly: event.target.checked })}
                  />
                  Read Only
                </label>
              </>
            ) : null}
          </Group>

          <Group title="Appearance">
            <div className="efd__field">
              <label>Placeholder</label>
              <input
                data-testid={FD.controlPropPlaceholder}
                value={control.placeholder}
                disabled={readOnly}
                onChange={(event) => onControlChange(control.id, { placeholder: event.target.value })}
              />
            </div>
            <div className="efd__field">
              <label>Help Text</label>
              <textarea
                data-testid={FD.controlPropHelpText}
                rows={2}
                value={control.helpText}
                disabled={readOnly}
                onChange={(event) => onControlChange(control.id, { helpText: event.target.value })}
              />
            </div>
          </Group>

          <Group title="Layout">
            <NumberField
              label="Column"
              testId={FD.controlPropCol}
              value={control.col}
              min={1}
              max={cardBounds(findCard(form, control.cardId)).columns}
              disabled={readOnly}
              onChange={(next) => onControlChange(control.id, { col: next }, "move")}
            />
            <NumberField
              label="Row"
              testId={FD.controlPropRow}
              value={control.row}
              min={1}
              max={CARD_MAX_ROWS}
              disabled={readOnly}
              onChange={(next) => onControlChange(control.id, { row: next }, "move")}
            />
            <NumberField
              label="Width (cols)"
              testId={FD.controlPropWidth}
              value={control.width}
              min={1}
              max={cardBounds(findCard(form, control.cardId)).columns}
              disabled={readOnly}
              onChange={(next) => onControlChange(control.id, { width: next }, "resize")}
            />
            <NumberField
              label="Height (rows)"
              testId={FD.controlPropHeight}
              value={control.height}
              min={1}
              max={CARD_MAX_ROWS}
              disabled={readOnly}
              onChange={(next) => onControlChange(control.id, { height: next }, "resize")}
            />
            <div className="efd__field efd__field--wide">
              <label>Card</label>
              <select
                data-testid={FD.controlPropCard}
                value={control.cardId}
                disabled={readOnly}
                onChange={(event) => onReassignControl(control.id, event.target.value)}
              >
                {(form.cards || []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>
          </Group>

          <div className="efd__prop-actions">
            <button
              type="button"
              className="efd__btn efd__btn--ghost efd__btn--danger"
              data-testid={FD.controlDelete}
              disabled={readOnly}
              onClick={() => onDeleteControl(control.id)}
            >
              Delete Control
            </button>
          </div>
        </>
      ) : null}
    </aside>
  );
}
